// services/IPVerification.js - Simple IP-based location verification
import { collection, getDocs, addDoc, doc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";

/**
 * Get the user's current public IP address
 * @returns {Promise<string>} - Public IP address
 */
const getCurrentIP = async () => {
    try {
        // Try CORS-friendly IP detection services
        const services = [
            {
                url: 'https://api64.ipify.org?format=json',
                parser: (data) => data.ip
            },
            {
                url: 'https://ipapi.co/json/',
                parser: (data) => data.ip
            },
            {
                url: 'https://api.bigdatacloud.net/data/client-ip',
                parser: (data) => data.ipString
            }
        ];

        for (const service of services) {
            try {
                const response = await fetch(service.url, {
                    method: 'GET',
                    mode: 'cors', // Explicitly set CORS mode
                    cache: 'no-store',
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    const ip = service.parser(data);

                    if (ip) {
                        console.log(`IP detected from ${service.url}: ${ip}`);
                        return ip;
                    }
                }
            } catch (serviceError) {
                console.warn(`IP service ${service.url} failed:`, serviceError);
                continue;
            }
        }

        throw new Error('All IP detection services failed');
    } catch (error) {
        console.error("Error getting current IP:", error);
        throw new Error(`Failed to detect current IP address: ${error.message}`);
    }
};

/**
 * Check if the user's current IP is approved for the organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<{isApproved: boolean, currentIP: string, message: string}>}
 */
export const checkIPLocation = async (orgId) => {
    try {
        // Get current IP address
        const currentIP = await getCurrentIP();

        // Get approved IPs from Firestore
        const approvedIPsRef = collection(db, "organizations", orgId, "approvedIPs");
        const snapshot = await getDocs(approvedIPsRef);

        const approvedIPs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));

        if (approvedIPs.length === 0) {
            return {
                isApproved: false,
                currentIP,
                message: "No approved IP addresses configured for this organization. Please contact your administrator."
            };
        }

        // Check if current IP matches any approved IP
        const matchingIP = approvedIPs.find(approved => approved.ipAddress === currentIP);

        if (matchingIP) {
            return {
                isApproved: true,
                currentIP,
                ipName: matchingIP.name || "Office Network",
                message: `Location verified: Connected from approved network "${matchingIP.name || "Office"}"`
            };
        } else {
            // Find the most recently added IP for comparison
            const latestApprovedIP = approvedIPs
                .sort((a, b) => (b.createdAt?.toDate?.() || new Date(b.createdAt)) - (a.createdAt?.toDate?.() || new Date(a.createdAt)))
            [0];

            return {
                isApproved: false,
                currentIP,
                expectedIP: latestApprovedIP?.ipAddress,
                message: `Location verification failed. Current IP (${currentIP}) is not approved for clock-in. Please connect from your office network.`
            };
        }
    } catch (error) {
        console.error("IP location check error:", error);
        return {
            isApproved: false,
            currentIP: null,
            error: error.message,
            message: `Error during location verification: ${error.message}`
        };
    }
};

/**
 * Register a new approved IP address for the organization
 * @param {string} orgId - Organization ID
 * @param {Object} ipDetails - IP details to register
 * @param {string} ipDetails.name - Display name for this IP (e.g., "Main Office")
 * @param {string} ipDetails.userId - Admin user ID who is registering this IP
 * @returns {Promise<string>} - ID of the newly created IP document
 */
export const registerApprovedIP = async (orgId, ipDetails) => {
    try {
        // Get current IP address
        const currentIP = await getCurrentIP();

        // Check if this IP is already registered
        const approvedIPsRef = collection(db, "organizations", orgId, "approvedIPs");
        const existingSnapshot = await getDocs(approvedIPsRef);

        const existingIP = existingSnapshot.docs.find(doc =>
            doc.data().ipAddress === currentIP
        );

        if (existingIP) {
            throw new Error(`IP address ${currentIP} is already registered as "${existingIP.data().name}"`);
        }

        // Create new approved IP record
        const ipData = {
            name: ipDetails.name || "Office Network",
            ipAddress: currentIP,
            createdAt: new Date(),
            createdBy: ipDetails.userId,
            isActive: true
        };

        const docRef = await addDoc(approvedIPsRef, ipData);

        return {
            id: docRef.id,
            ipAddress: currentIP,
            message: `Successfully registered IP ${currentIP} as "${ipData.name}"`
        };
    } catch (error) {
        console.error("Error registering IP:", error);
        throw error;
    }
};

/**
 * Remove an approved IP address
 * @param {string} orgId - Organization ID  
 * @param {string} ipId - IP record ID to remove
 * @returns {Promise<void>}
 */
export const removeApprovedIP = async (orgId, ipId) => {
    try {
        const ipRef = doc(db, "organizations", orgId, "approvedIPs", ipId);
        await deleteDoc(ipRef);
    } catch (error) {
        console.error("Error removing approved IP:", error);
        throw error;
    }
};

/**
 * Get all approved IPs for an organization (admin function)
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} - List of approved IPs
 */
export const getApprovedIPs = async (orgId) => {
    try {
        const approvedIPsRef = collection(db, "organizations", orgId, "approvedIPs");
        const snapshot = await getDocs(approvedIPsRef);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
        }));
    } catch (error) {
        console.error("Error fetching approved IPs:", error);
        throw error;
    }
};